import React from 'react';
import styled from '@emotion/styled';
import { darken, shade, tint, transparentize } from 'polished';
import capitalize from 'lodash/capitalize';

import { useComponentTheme } from '../useComponentTheme';
import { Tooltip } from '../tooltip';
import { Icon } from '../icons/Icon';
import { withSpacingStyles } from '../withSpacingStyles';

type Props = {
  ariaLabel: string,
  ariaPressed: boolean,
  fill: string,
  icon: string,
  isCircle: boolean,
  backgroundColor?: string,
  borderColor?: string,
  onClick?: Function,
  dataTest?: ?string,
  className?: string,
  block?: boolean,
  tooltip?: string,
  touchTooltip?: boolean,
  spin?: boolean,
  size: 'xsmall' | 'small' | 'large' | 'xlarge' | 'xxlarge',
  type?: 'submit' | 'button' | 'reset',
  mode?: 'primary' | 'danger' | 'outline' | 'light' | 'dark' | 'ghost',
  withoutSpace?: boolean,
  autoHeight?: boolean,
};

const buttonStyles = {
  xsmall: {
    width: '32px',
    height: '32px',
    fontSize: theme => theme.fontSize.sm,
  },
  small: {
    width: '40px',
    height: '40px',
    fontSize: theme => theme.fontSize.md,
  },
  large: {
    width: '48px',
    height: '48px',
    fontSize: theme => theme.fontSize.xxl,
  },
  xlarge: {
    width: '48px',
    height: '48px',
    fontSize: theme => theme.fontSize.xxl,
  },
  xxlarge: {
    width: '64px',
    height: '64px',
    fontSize: theme => theme.fontSize.xl,
  },
};

const defaultModes = ['primary', 'danger', 'outline', 'light', 'dark'];

export const _IconButton = ({
  ariaLabel,
  ariaPressed,
  className,
  icon,
  fill,
  backgroundColor,
  borderColor,
  onClick,
  dataTest,
  type,
  isCircle = true,
  size,
  block,
  tooltip,
  touchTooltip,
  spin,
  mode = 'primary',
  withoutSpace,
  autoHeight,
  ...rest
}: Props) => {
  // If it's necessary to override the styles of one of the default modes, a custom mode with a name that ends in one of
  // the default modes (capitalised) can be used, e.g. `HomepagePrimary` or `HomepageLight`. This will apply all of the
  // styles from the default mode, and then individual styles can be overriden as normal in the component theming.
  const modeKey = defaultModes.includes(mode) ? capitalize(mode) : mode;
  const modeType = modeKey.split(/(?=[A-Z])/).slice(-1)[0];
  const defaultComponentTheme = useComponentTheme(theme => theme.components.Button, modeType);
  const customComponentTheme = useComponentTheme(theme => theme.components.Button, modeKey);
  const componentTheme = { ...defaultComponentTheme, ...customComponentTheme };
  let Tag;

  switch (modeType.toLowerCase()) {
    case 'primary':
      Tag = PrimaryButton;
      break;
    case 'danger':
      Tag = DangerButton;
      break;
    case 'outline':
      Tag = OutlineButton;
      break;
    case 'light':
      Tag = LightButton;
      break;
    case 'dark':
      Tag = DarkButton;
      break;
    default:
      Tag = RoundedButton;
      break;
  }

  const iconButton = (
    <Tag
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      isCircle={isCircle}
      onClick={onClick}
      type={type}
      mode={mode}
      className={className}
      data-test={dataTest}
      size={size}
      block={block}
      componentTheme={componentTheme}
      withoutSpace={withoutSpace}
      autoHeight={autoHeight}
      {...rest}
    >
      <Icon spin={spin} name={icon} fill={fill} onClick={e => rest.disabled && e.stopPropagation()} />
    </Tag>
  );

  if (tooltip) {
    return (
      <Tooltip content={tooltip} touch={touchTooltip}>
        {iconButton}
      </Tooltip>
    );
  }
  return iconButton;
};

export const IconButton = withSpacingStyles(_IconButton);

IconButton.defaultProps = {
  icon: 'Plus',
  backgroundColor: 'transparent',
  fill: 'currentColor',
  size: 'small',
};

const RoundedButton = styled('button')`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: ${({ size, block }) => (block ? '100%' : buttonStyles[size].width)};
  height: ${({ size, block }) => !block && buttonStyles[size].height};
  flex: ${({ autoHeight }) => `${autoHeight ? '1' : '0'} 0 auto`};
  border-radius: ${({ block, isCircle, theme }) =>
    block || !isCircle ? theme.borderRadius.medium : theme.borderRadius.circle};
  cursor: pointer;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border: ${({ borderColor }) => `2px solid ${borderColor || 'transparent'}`};
  font-size: ${({ theme, size }) => buttonStyles[size].fontSize(theme)};
  padding: 0;
  transition: ${({ theme }) => theme.motion.transition};
  line-height: 1;

  :focus {
    outline: none;
    box-shadow: ${({ theme }) => `0 0 0 3px ${transparentize(0.5, theme.color.brand)}`};
  }

  :disabled {
    cursor: default;
    opacity: 0.25;
  }

  ${({ size, withoutSpace }) =>
    size === 'xsmall' &&
    `
    position: relative;

    &:after {
      content: '';
      position: absolute;
      width: ${withoutSpace ? '32px' : '40px'};
      height: ${withoutSpace ? '32px' : '40px'};
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  `}

  ${({ size, theme }) =>
    size !== 'xxlarge' &&
    `
    @media (${theme.breakpoint.kiosk}) {
      width: 48px;
      height: 48px;
    }
  `}
`;

const PrimaryButton = styled(RoundedButton)`
  background-color: ${({ theme, componentTheme }) => componentTheme.backgroundColor || theme.color.brand};
  border-color: ${({ theme, componentTheme }) => componentTheme.borderColor || theme.color.brand};
  color: ${({ componentTheme }) => componentTheme.color};
  border-radius: ${({ componentTheme, block }) => block && componentTheme.borderRadius};

  :hover {
    background-color: ${({ theme, componentTheme }) =>
      shade(0.05, `${componentTheme.backgroundColor || theme.color.brand}`)};
    border-color: ${({ theme, componentTheme }) =>
      componentTheme.borderColor && shade(0.05, `${componentTheme.borderColor || theme.color.brand}`)};
    color: ${({ componentTheme }) => componentTheme.color};
  }

  :active {
    background-color: ${({ theme, componentTheme }) =>
      shade(0.08, `${componentTheme.backgroundColor || theme.color.brand}`)};
    color: ${({ componentTheme }) => componentTheme.color};
    border-color: ${({ theme, componentTheme }) =>
      componentTheme.borderColor && shade(0.08, `${componentTheme.borderColor || theme.color.brand}`)};
  }

  :disabled {
    color: ${({ theme }) => tint(0.6, theme.color.grey)};
    background-color: ${({ theme }) => tint(0.6, theme.color.lightGrey)};
    border-color: ${({ theme }) => tint(0.6, theme.color.lightGrey)};
    color: ${({ theme }) => theme.color.white};
  }
`;

const DangerButton = styled(RoundedButton)`
  background-color: ${({ theme }) => theme.color.danger};
  border-color: ${({ theme }) => theme.color.danger};
  color: ${({ theme }) => theme.color.white};

  :hover {
    background-color: ${({ theme }) => shade(0.05, `${theme.color.danger}`)};
  }

  :active {
    background-color: ${({ theme }) => shade(0.08, `${theme.color.danger}`)};
  }

  :disabled {
    color: ${({ theme }) => tint(0.6, theme.color.grey)};
    background-color: ${({ theme }) => tint(0.6, theme.color.lightGrey)};
    border-color: ${({ theme }) => tint(0.6, theme.color.lightGrey)};
  }
`;

const OutlineButton = styled(RoundedButton)`
  background-color: ${({ componentTheme }) => componentTheme.backgroundColor};
  border-color: ${({ theme, componentTheme }) => componentTheme.borderColor || theme.color.brand};
  color: ${({ componentTheme }) => componentTheme.color};
  border-radius: ${({ componentTheme, block }) => block && componentTheme.borderRadius};

  :hover {
    background-color: ${({ theme, componentTheme }) =>
      shade(0.05, `${componentTheme.backgroundColor || theme.color.brand}`)};
    border-color: ${({ theme, componentTheme }) => componentTheme.borderColor || theme.color.brand};
    color: ${({ theme, componentTheme }) => componentTheme.color || theme.color.brand};
  }

  :active {
    background-color: ${({ theme, componentTheme }) =>
      shade(0.08, componentTheme.backgroundColor || theme.color.brand)};
    border-color: ${({ theme, componentTheme }) => componentTheme.borderColor || theme.color.brand};
    color: ${({ theme, componentTheme }) => componentTheme.color || theme.color.brand};
  }

  :disabled {
    color: ${({ theme }) => tint(0.6, theme.color.grey)};
    border-color: ${({ theme }) => tint(0.6, theme.color.lightGrey)};
  }
`;

const LightButton = styled(RoundedButton)`
  background-color: ${({ componentTheme }) => componentTheme.backgroundColor};
  border-color: ${({ componentTheme }) => componentTheme.borderColor};
  color: ${({ componentTheme }) => componentTheme.color};
  border-radius: ${({ componentTheme, block }) => block && componentTheme.borderRadius};

  &[aria-pressed="true"] {
    background-color: ${({ componentTheme: { selected }, theme }) =>
      selected.backgroundColor || tint(0.85, theme.color.brand)};
    border-color: ${({ componentTheme: { selected }, theme }) => selected.borderColor || theme.color.brand};
    color: ${({ componentTheme: { selected }, theme }) => selected.color || theme.color.brand};
  }

  :hover {
    background-color: ${({ componentTheme }) => darken(0.25, componentTheme.backgroundColor)};
    border-color: ${({ componentTheme }) => componentTheme.borderColor};
    color: ${({ componentTheme }) => componentTheme.color};
  }

  :active {
    background-color: ${({ theme }) => transparentize(0.92, theme.color.black)};
    border-color: ${({ componentTheme }) => componentTheme.borderColor};
    color: ${({ componentTheme }) => componentTheme.color};
  }

  :disabled {
    color: ${({ theme }) => tint(0.6, theme.color.grey)};
    border-color: ${({ theme }) => tint(0.6, theme.color.lightGrey)};
  }
`;

const DarkButton = styled(RoundedButton)`
  border: ${({ theme }) => theme.stroke.default};
  background: ${({ theme }) => theme.color.black};
  color: ${({ theme }) => theme.color.white};
`;
